import { ProjectState } from "@tapeout/sdk";
import type { Address } from "viem";

import type {
  BatchKind,
  MintQuantityMode,
  ProjectSnapshot,
  TapeoutMode,
  WalletRow,
} from "./batchTypes.js";

interface BaseBatchPlan {
  readonly addresses: readonly Address[];
  readonly skippedAddresses: readonly Address[];
  readonly paymentTotal: bigint;
  readonly nativeFeeTotal: bigint;
  readonly estimatedWrites: number;
}

export interface MintAllocation {
  readonly address: Address;
  readonly quantity: bigint;
  readonly chunks: readonly bigint[];
}

export interface MintBatchPlan extends BaseBatchPlan {
  readonly kind: "mint";
  readonly quantityMode: MintQuantityMode;
  readonly allocations: readonly MintAllocation[];
  readonly totalMintQuantity: bigint;
}

export interface TapeoutAllocation {
  readonly address: Address;
  readonly nandIds: readonly bigint[];
  readonly groups: readonly (readonly [bigint, bigint, bigint, bigint, bigint])[];
}

export interface TapeoutBatchPlan extends BaseBatchPlan {
  readonly kind: "tapeout";
  readonly tapeoutMode: TapeoutMode;
  readonly allocations: readonly TapeoutAllocation[];
  readonly totalTapeoutCount: number;
}

export type BatchPlan = MintBatchPlan | TapeoutBatchPlan;

const TAPEOUT_FEE = 2_000_000_000_000_000n;
export const MIN_MINT_QUANTITY = 5n;
export const MAX_MINT_QUANTITY = 195n;
export const MINT_QUANTITY_STEP = 5n;
export const MAX_MINT_PER_TRANSACTION = 50n;

function validateMintQuantity(quantity: bigint, label: string): void {
  if (
    quantity < MIN_MINT_QUANTITY ||
    quantity > MAX_MINT_QUANTITY ||
    quantity % MINT_QUANTITY_STEP !== 0n
  ) {
    throw new Error(`${label}必须是 5–195 之间且为 5 的倍数`);
  }
}

export function splitMintQuantity(quantity: bigint): readonly bigint[] {
  validateMintQuantity(quantity, "Mint 数量");
  const chunks: bigint[] = [];
  let remaining = quantity;
  while (remaining > 0n) {
    const chunk = remaining > MAX_MINT_PER_TRANSACTION ? MAX_MINT_PER_TRANSACTION : remaining;
    chunks.push(chunk);
    remaining -= chunk;
  }
  return chunks;
}

function randomQuantity(min: bigint, max: bigint, random: () => number): bigint {
  validateMintQuantity(min, "随机下限");
  validateMintQuantity(max, "随机上限");
  if (min > max) throw new Error("随机数量下限不能大于上限");
  const slots = Number((max - min) / MINT_QUANTITY_STEP + 1n);
  const value = random();
  if (!Number.isFinite(value) || value < 0 || value >= 1) {
    throw new Error("随机数生成器返回了无效结果");
  }
  return min + BigInt(Math.floor(value * slots)) * MINT_QUANTITY_STEP;
}

function toGroups(
  ids: readonly bigint[],
  count: number,
): readonly (readonly [bigint, bigint, bigint, bigint, bigint])[] {
  return Array.from({ length: count }, (_, index) => {
    const group = ids.slice(index * 5, index * 5 + 5);
    if (group.length !== 5) throw new Error("流片 NAND 分组不完整");
    return group as unknown as readonly [bigint, bigint, bigint, bigint, bigint];
  });
}

export interface BatchPlanInput {
  readonly kind: BatchKind;
  readonly rows: readonly WalletRow[];
  readonly snapshot: ProjectSnapshot;
  readonly mintQuantityMode: MintQuantityMode;
  readonly mintQuantity: bigint;
  readonly mintRandomMin: bigint;
  readonly mintRandomMax: bigint;
  readonly tapeoutMode: TapeoutMode;
  readonly tapeoutCount: number;
  readonly random?: () => number;
}

export function createBatchPlan(input: BatchPlanInput & { readonly kind: "mint" }): MintBatchPlan;
export function createBatchPlan(
  input: BatchPlanInput & { readonly kind: "tapeout" },
): TapeoutBatchPlan;
export function createBatchPlan(input: BatchPlanInput): BatchPlan;
export function createBatchPlan(input: BatchPlanInput): BatchPlan {
  const selected = input.rows.filter((row) => row.selected);
  if (selected.length === 0) throw new Error("请至少选择一个钱包");
  if (selected.some((row) => row.stage === "uncertain")) {
    throw new Error("存在结果待核对的交易，请先在区块浏览器确认后再发起新批次");
  }

  if (input.kind === "mint") {
    if (input.snapshot.state !== ProjectState.FUNDRAISING) {
      throw new Error("项目当前不在募资阶段，无法 Mint NAND");
    }
    if (input.mintQuantityMode === "fixed") {
      validateMintQuantity(input.mintQuantity, "每个钱包的 Mint 数量");
    } else {
      validateMintQuantity(input.mintRandomMin, "随机下限");
      validateMintQuantity(input.mintRandomMax, "随机上限");
      if (input.mintRandomMin > input.mintRandomMax) {
        throw new Error("随机数量下限不能大于上限");
      }
    }
    const allocations = selected.map((row) => {
      const quantity =
        input.mintQuantityMode === "fixed"
          ? input.mintQuantity
          : randomQuantity(input.mintRandomMin, input.mintRandomMax, input.random ?? Math.random);
      return { address: row.address, quantity, chunks: splitMintQuantity(quantity) };
    });
    const totalMintQuantity = allocations.reduce((total, item) => total + item.quantity, 0n);
    if (input.snapshot.sold + totalMintQuantity > input.snapshot.nandCap) {
      throw new Error("本批次总量超过项目剩余 NAND");
    }
    return {
      kind: "mint",
      quantityMode: input.mintQuantityMode,
      allocations,
      addresses: allocations.map((item) => item.address),
      skippedAddresses: [],
      totalMintQuantity,
      paymentTotal: input.snapshot.nandPrice * totalMintQuantity,
      nativeFeeTotal: 0n,
      estimatedWrites:
        allocations.reduce((total, item) => total + item.chunks.length, 0) +
        (input.snapshot.paymentKind === 1 ? selected.length : 0),
    };
  }

  if (
    input.tapeoutMode === "count" &&
    (!Number.isSafeInteger(input.tapeoutCount) || input.tapeoutCount < 1)
  ) {
    throw new Error("每个钱包的流片次数至少为 1");
  }
  if (input.tapeoutMode === "count") {
    const required = input.tapeoutCount * 5;
    const missing = selected.find((row) => row.nandIds.length < required);
    if (missing !== undefined) {
      throw new Error(`钱包 ${missing.address.slice(0, 8)}… 缺少 NAND ID，需要 ${required} 枚`);
    }
  }
  const allocations = selected.flatMap((row) => {
    const count =
      input.tapeoutMode === "all" ? Math.floor(row.nandIds.length / 5) : input.tapeoutCount;
    if (count === 0) return [];
    const nandIds = row.nandIds.slice(0, count * 5);
    return [{ address: row.address, nandIds, groups: toGroups(nandIds, count) }];
  });
  if (allocations.length === 0) {
    throw new Error("已选钱包没有足够的 NAND；每次流片需要 5 枚");
  }
  const active = new Set(allocations.map((item) => item.address.toLowerCase()));
  const skippedAddresses = selected
    .filter((row) => !active.has(row.address.toLowerCase()))
    .map((row) => row.address);
  const totalTapeoutCount = allocations.reduce((total, item) => total + item.groups.length, 0);
  return {
    kind: "tapeout",
    tapeoutMode: input.tapeoutMode,
    allocations,
    addresses: allocations.map((item) => item.address),
    skippedAddresses,
    totalTapeoutCount,
    paymentTotal: 0n,
    nativeFeeTotal: TAPEOUT_FEE * BigInt(totalTapeoutCount),
    estimatedWrites: totalTapeoutCount * 2,
  };
}
